import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { db } from '../config';
import { collection, doc, getDoc, setDoc, updateDoc, query, where, getDocs } from 'firebase/firestore';
import { QUIZ_QUESTIONS } from '../data/quizQuestions';
import './DailyQuiz.css';

interface Question {
  id: string;
  question: string;
  options: string[];
  correctAnswer: number;
  explanation: string;
  category: string;
  difficulty: 'easy' | 'medium' | 'hard';
  points: number;
}

interface QuizResult {
  userId: string;
  date: string;
  score: number;
  totalQuestions: number;
  correctAnswers: number;
  timeSpent: number;
  completedAt: Date;
  answers: {
    questionId: string;
    userAnswer: number;
    correct: boolean;
  }[];
}

interface UserQuizStats {
  userId: string;
  totalQuizzes: number;
  totalScore: number;
  averageScore: number;
  currentStreak: number;
  bestStreak: number;
  lastQuizDate: string;
  badges: string[];
}

const DailyQuiz: React.FC = () => {
  const { user } = useAuth();
  const [questions, setQuestions] = useState<Question[]>([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [showResult, setShowResult] = useState(false);
  const [quizCompleted, setQuizCompleted] = useState(false);
  const [score, setScore] = useState(0);
  const [answers, setAnswers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [alreadyCompleted, setAlreadyCompleted] = useState(false);
  const [userStats, setUserStats] = useState<UserQuizStats | null>(null);
  const [startTime] = useState(new Date());

  useEffect(() => {
    if (user?.uid) {
      checkIfCompletedToday();
      loadUserStats();
      loadTodaysQuestions();
    }
  }, [user?.uid]);

  const checkIfCompletedToday = async () => {
    if (!user?.uid) return;

    try {
      const today = new Date().toISOString().split('T')[0];
      const resultsRef = collection(db, 'quizResults');
      const q = query(resultsRef, where('userId', '==', user.uid), where('date', '==', today));
      const snapshot = await getDocs(q);

      if (!snapshot.empty) {
        setAlreadyCompleted(true);
        const result = snapshot.docs[0].data() as QuizResult;
        setScore(result.score);
        setQuizCompleted(true);
      }
    } catch (error) {
      console.error('Error checking quiz completion:', error);
    }
  };

  const loadUserStats = async () => {
    if (!user?.uid) return;

    try {
      const statsRef = doc(db, 'quizStats', user.uid);
      const statsSnap = await getDoc(statsRef);

      if (statsSnap.exists()) {
        setUserStats(statsSnap.data() as UserQuizStats);
      } else {
        const initialStats: UserQuizStats = {
          userId: user.uid,
          totalQuizzes: 0,
          totalScore: 0,
          averageScore: 0,
          currentStreak: 0,
          bestStreak: 0,
          lastQuizDate: '',
          badges: []
        };
        await setDoc(statsRef, initialStats);
        setUserStats(initialStats);
      }
    } catch (error) {
      console.error('Error loading user stats:', error);
    }
  };

  const loadTodaysQuestions = async () => {
    try {
      // Get user's question history to avoid repeats
      const historyRef = doc(db, 'quizHistory', user?.uid || 'guest');
      const historySnap = await getDoc(historyRef);
      
      let recentQuestionIds: string[] = [];
      if (historySnap.exists()) {
        const history = historySnap.data();
        recentQuestionIds = history.recentQuestions || [];
      }

      // Filter out recently asked questions (last 20 questions)
      const availableQuestions = QUIZ_QUESTIONS.filter(
        q => !recentQuestionIds.includes(q.id)
      );

      // If we've used most questions, reset history
      if (availableQuestions.length < 10) {
        recentQuestionIds = [];
        console.log('📚 Question pool refreshed - all questions available again!');
      }

      // Select 5 random questions from available pool
      const shuffled = (availableQuestions.length > 0 ? availableQuestions : QUIZ_QUESTIONS)
        .sort(() => Math.random() - 0.5);
      const todaysQuestions = shuffled.slice(0, 5);

      // Save today's question IDs to history
      const todayIds = todaysQuestions.map(q => q.id);
      const updatedHistory = [...todayIds, ...recentQuestionIds].slice(0, 20); // Keep last 20

      await setDoc(historyRef, {
        userId: user?.uid || 'guest',
        recentQuestions: updatedHistory,
        lastUpdated: new Date()
      }, { merge: true });

      setQuestions(todaysQuestions);
      setLoading(false);
      
      console.log('📚 Today\'s quiz loaded:', todaysQuestions.length, 'questions');
      console.log('🔍 Categories:', todaysQuestions.map(q => q.category).join(', '));
    } catch (error) {
      console.error('Error loading questions:', error);
      // Fallback to random questions
      const fallbackQuestions = QUIZ_QUESTIONS.sort(() => Math.random() - 0.5).slice(0, 5);
      setQuestions(fallbackQuestions);
      setLoading(false);
    }
  };


  const handleAnswerSelect = (answerIndex: number) => {
    if (showResult) return; // Don't allow changing after showing result
    setSelectedAnswer(answerIndex);
  };

  const handleSubmitAnswer = () => {
    if (selectedAnswer === null) {
      alert('Please select an answer!');
      return;
    }

    const currentQuestion = questions[currentQuestionIndex];
    const isCorrect = selectedAnswer === currentQuestion.correctAnswer;

    // Record answer
    const answerRecord = {
      questionId: currentQuestion.id,
      userAnswer: selectedAnswer,
      correct: isCorrect
    };
    setAnswers([...answers, answerRecord]);

    // Update score
    if (isCorrect) {
      setScore(score + currentQuestion.points);
    }

    // Show result
    setShowResult(true);
  };

  const handleNextQuestion = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      setSelectedAnswer(null);
      setShowResult(false);
    } else {
      // Quiz completed
      completeQuiz();
    }
  };

  const completeQuiz = async () => {
    if (!user?.uid) return;

    try {
      const today = new Date().toISOString().split('T')[0];
      const endTime = new Date();
      const timeSpent = Math.floor((endTime.getTime() - startTime.getTime()) / 1000);

      // Save quiz result
      const result: QuizResult = {
        userId: user.uid,
        date: today,
        score: score,
        totalQuestions: questions.length,
        correctAnswers: answers.filter(a => a.correct).length,
        timeSpent: timeSpent,
        completedAt: new Date(),
        answers: answers
      };

      await setDoc(doc(db, 'quizResults', `${user.uid}_${today}`), result);

      // Update user stats
      const statsRef = doc(db, 'quizStats', user.uid);
      const statsSnap = await getDoc(statsRef);

      if (statsSnap.exists()) {
        const currentStats = statsSnap.data() as UserQuizStats;
        const newTotalQuizzes = currentStats.totalQuizzes + 1;
        const newTotalScore = currentStats.totalScore + score;
        const newAverageScore = Math.round(newTotalScore / newTotalQuizzes);

        // Calculate streak
        const lastDate = new Date(currentStats.lastQuizDate);
        const todayDate = new Date(today);
        const dayDiff = Math.floor((todayDate.getTime() - lastDate.getTime()) / (1000 * 60 * 60 * 24));
        
        let newStreak = currentStats.currentStreak;
        if (dayDiff === 1) {
          newStreak += 1; // Consecutive day
        } else if (dayDiff > 1) {
          newStreak = 1; // Streak broken, restart
        }

        const newBestStreak = Math.max(newStreak, currentStats.bestStreak);

        // Check for new badges
        const newBadges = [...currentStats.badges];
        if (newStreak >= 7 && !newBadges.includes('week-streak')) {
          newBadges.push('week-streak');
        }
        if (newStreak >= 30 && !newBadges.includes('month-streak')) {
          newBadges.push('month-streak');
        }
        if (newTotalQuizzes >= 10 && !newBadges.includes('quiz-master')) {
          newBadges.push('quiz-master');
        }
        if (newAverageScore >= 80 && !newBadges.includes('high-scorer')) {
          newBadges.push('high-scorer');
        }

        await updateDoc(statsRef, {
          totalQuizzes: newTotalQuizzes,
          totalScore: newTotalScore,
          averageScore: newAverageScore,
          currentStreak: newStreak,
          bestStreak: newBestStreak,
          lastQuizDate: today,
          badges: newBadges
        });

        setUserStats({
          ...currentStats,
          totalQuizzes: newTotalQuizzes,
          totalScore: newTotalScore,
          averageScore: newAverageScore,
          currentStreak: newStreak,
          bestStreak: newBestStreak,
          lastQuizDate: today,
          badges: newBadges
        });
      }

      setQuizCompleted(true);
    } catch (error) {
      console.error('Error completing quiz:', error);
    }
  };

  const currentQuestion = questions[currentQuestionIndex];

  if (loading) {
    return (
      <div className="quiz-loading">
        <div className="spinner"></div>
        <p>Loading today's quiz...</p>
      </div>
    );
  }

  if (alreadyCompleted) {
    return (
      <div className="quiz-completed-today">
        <div className="completed-icon">🎉</div>
        <h2>Quiz Already Completed!</h2>
        <p>You've already completed today's quiz.</p>
        <div className="today-score">
          <div className="score-label">Your Score:</div>
          <div className="score-value">{score}%</div>
        </div>
        <div className="comeback-message">
          <p>Come back tomorrow for a new quiz!</p>
          <p className="countdown">⏰ Next quiz available in: {getTimeUntilMidnight()}</p>
        </div>
        {userStats && (
          <div className="stats-summary">
            <h3>Your Quiz Stats</h3>
            <div className="stats-grid">
              <div className="stat-item">
                <div className="stat-icon">📊</div>
                <div className="stat-value">{userStats.totalQuizzes}</div>
                <div className="stat-label">Quizzes Taken</div>
              </div>
              <div className="stat-item">
                <div className="stat-icon">⭐</div>
                <div className="stat-value">{userStats.averageScore}%</div>
                <div className="stat-label">Average Score</div>
              </div>
              <div className="stat-item">
                <div className="stat-icon">🔥</div>
                <div className="stat-value">{userStats.currentStreak}</div>
                <div className="stat-label">Current Streak</div>
              </div>
              <div className="stat-item">
                <div className="stat-icon">🏆</div>
                <div className="stat-value">{userStats.badges.length}</div>
                <div className="stat-label">Badges Earned</div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  if (quizCompleted) {
    const correctCount = answers.filter(a => a.correct).length;
    const percentage = Math.round((correctCount / questions.length) * 100);

    return (
      <div className="quiz-results">
        <div className="results-header">
          <div className="results-icon">
            {percentage >= 80 ? '🌟' : percentage >= 60 ? '👍' : '💪'}
          </div>
          <h2>Quiz Completed!</h2>
          <div className="final-score">
            <div className="score-circle">
              <div className="score-number">{percentage}%</div>
              <div className="score-text">{correctCount}/{questions.length} correct</div>
            </div>
          </div>
        </div>

        <div className="performance-message">
          {percentage >= 80 && <p className="excellent">🌟 Excellent! You're a knowledge master!</p>}
          {percentage >= 60 && percentage < 80 && <p className="good">👍 Good job! Keep learning!</p>}
          {percentage < 60 && <p className="improve">💪 Keep practicing! You'll improve!</p>}
        </div>

        {userStats && (
          <div className="stats-update">
            <h3>Your Progress</h3>
            <div className="stats-grid">
              <div className="stat-item">
                <div className="stat-icon">🔥</div>
                <div className="stat-value">{userStats.currentStreak}</div>
                <div className="stat-label">Day Streak</div>
              </div>
              <div className="stat-item">
                <div className="stat-icon">⭐</div>
                <div className="stat-value">{userStats.averageScore}%</div>
                <div className="stat-label">Average Score</div>
              </div>
              <div className="stat-item">
                <div className="stat-icon">📊</div>
                <div className="stat-value">{userStats.totalQuizzes}</div>
                <div className="stat-label">Total Quizzes</div>
              </div>
              <div className="stat-item">
                <div className="stat-icon">🏆</div>
                <div className="stat-value">{userStats.badges.length}</div>
                <div className="stat-label">Badges</div>
              </div>
            </div>
          </div>
        )}

        <div className="comeback-message">
          <p>Come back tomorrow for a new quiz!</p>
          <p className="countdown">⏰ Next quiz in: {getTimeUntilMidnight()}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="daily-quiz">
      <div className="quiz-header">
        <h2>📚 Daily Knowledge Quiz</h2>
        <div className="quiz-progress">
          Question {currentQuestionIndex + 1} of {questions.length}
        </div>
      </div>

      {userStats && (
        <div className="quiz-stats-bar">
          <div className="stat-mini">🔥 {userStats.currentStreak} day streak</div>
          <div className="stat-mini">⭐ {userStats.averageScore}% avg</div>
          <div className="stat-mini">📊 {userStats.totalQuizzes} quizzes</div>
        </div>
      )}

      <div className="question-card">
        <div className="question-header">
          <span className="category-badge">{currentQuestion.category}</span>
          <span className="difficulty-badge">{currentQuestion.difficulty}</span>
          <span className="points-badge">+{currentQuestion.points} points</span>
        </div>

        <div className="question-text">
          {currentQuestion.question}
        </div>

        <div className="options-list">
          {currentQuestion.options.map((option, index) => (
            <button
              key={index}
              className={`option-button ${
                selectedAnswer === index ? 'selected' : ''
              } ${
                showResult && index === currentQuestion.correctAnswer ? 'correct' : ''
              } ${
                showResult && selectedAnswer === index && index !== currentQuestion.correctAnswer ? 'incorrect' : ''
              }`}
              onClick={() => handleAnswerSelect(index)}
              disabled={showResult}
            >
              <span className="option-letter">{String.fromCharCode(65 + index)}</span>
              <span className="option-text">{option}</span>
              {showResult && index === currentQuestion.correctAnswer && <span className="check-icon">✓</span>}
              {showResult && selectedAnswer === index && index !== currentQuestion.correctAnswer && <span className="cross-icon">✗</span>}
            </button>
          ))}
        </div>

        {showResult && (
          <div className={`explanation ${selectedAnswer === currentQuestion.correctAnswer ? 'correct-explanation' : 'incorrect-explanation'}`}>
            <div className="explanation-header">
              {selectedAnswer === currentQuestion.correctAnswer ? '✅ Correct!' : '❌ Incorrect'}
            </div>
            <div className="explanation-text">
              {currentQuestion.explanation}
            </div>
          </div>
        )}
      </div>

      <div className="quiz-actions">
        {!showResult ? (
          <button 
            className="submit-answer-btn"
            onClick={handleSubmitAnswer}
            disabled={selectedAnswer === null}
          >
            Submit Answer
          </button>
        ) : (
          <button 
            className="next-question-btn"
            onClick={handleNextQuestion}
          >
            {currentQuestionIndex < questions.length - 1 ? 'Next Question →' : 'Finish Quiz 🎉'}
          </button>
        )}
      </div>

      <div className="score-display">
        Current Score: {score} points
      </div>
    </div>
  );
};

const getTimeUntilMidnight = (): string => {
  const now = new Date();
  const midnight = new Date();
  midnight.setHours(24, 0, 0, 0);
  const diff = midnight.getTime() - now.getTime();
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  return `${hours}h ${minutes}m`;
};

export default DailyQuiz;
